/** MemberLogin · /login · email+password + Google sign-in
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gold = "#FFD700";
const teal = "#00FFCC";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputStyle = {
  width: "100%", minHeight: 46, padding: "0 14px", fontSize: 15,
  background: "rgba(4,5,10,0.7)", border: "1px solid rgba(255,215,0,0.35)",
  borderRadius: 8, color: "#e0e0e0",
};

export default function MemberLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("reset");
    if (t) { setResetToken(t); setMode("reset"); }
  }, []);

  const post = async (path, body) => {
    const r = await fetch(`${API}/auth/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatApiErrorDetail(data.detail));
    return data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "forgot") {
        const d = await post("forgot-password", { email });
        setNotice(d.message || "If that email is registered, a reset link has been sent.");
      } else if (mode === "reset") {
        await post("reset-password", { token: resetToken, new_password: password });
        setNotice("Password updated. Please sign in with your new password.");
        setPassword("");
        setMode("login");
        window.history.replaceState(null, "", window.location.pathname);
      } else {
        const user = await post(mode === "login" ? "login" : "register",
          mode === "login" ? { email, password } : { email, password, name });
        navigate("/", { state: { user } });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-screen px-4 py-14 max-w-[460px] mx-auto" data-testid="member-login-page">
      <p className="text-center font-mono uppercase tracking-[0.26em] mb-2" style={{ fontSize: 11, color: teal }}>
        Federation Membership
      </p>
      <h1 className="font-serif text-center mb-8" style={{ fontSize: "clamp(28px,4vw,40px)", color: gold, textShadow: "0 0 22px rgba(255,215,0,0.4)" }}>
        {mode === "register" ? "Join the Federation" : mode === "forgot" ? "Recover Access" : mode === "reset" ? "Set a New Password" : "Member Sign In"}
      </h1>

      <div className="p-6" style={{ background: "#111118", border: "1px solid rgba(255,215,0,0.35)", borderRadius: 14 }}>
        {mode !== "reset" && (
          <>
            <button
              type="button"
              data-testid="login-google-btn"
              onClick={googleSignIn}
              className="w-full font-mono uppercase tracking-[0.2em] mb-5"
              style={{ minHeight: 48, fontSize: 13, color: "#0a0a0f", background: "linear-gradient(135deg,#00FFCC,#FFD700)", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700 }}
            >
              ◈ Continue with Google
            </button>
            <p className="text-center font-mono uppercase tracking-[0.24em] mb-5" style={{ fontSize: 10, color: "rgba(224,224,224,0.45)" }}>
              — or with email —
            </p>
          </>
        )}

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="login-name-input" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Your name" style={inputStyle} className="font-mono" />
          )}
          {mode !== "reset" && (
            <input data-testid="login-email-input" type="email" required value={email}
                   onChange={(e) => setEmail(e.target.value)} placeholder="Email address" style={inputStyle} className="font-mono" />
          )}
          {mode !== "forgot" && (
            <input data-testid="login-password-input" type="password" required value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   placeholder={mode === "reset" ? "New password (min 6 characters)" : "Password (min 6 characters)"}
                   style={inputStyle} className="font-mono" />
          )}
          {error && <p data-testid="login-error" className="font-mono" style={{ fontSize: 12, color: "#ff5470" }}>{error}</p>}
          {notice && <p data-testid="login-notice" className="font-mono" style={{ fontSize: 12, color: teal }}>{notice}</p>}
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy}
            className="w-full font-mono uppercase tracking-[0.22em]"
            style={{ minHeight: 48, fontSize: 13, fontWeight: 700, color: "#0a0a0f", background: "linear-gradient(135deg,#D4AF37,#FFD700)", border: "none", borderRadius: 8, cursor: "pointer" }}
          >
            {busy ? "Working…" : mode === "login" ? "⎆ Sign In" : mode === "register" ? "✦ Create Account" : mode === "reset" ? "Update Password" : "Send Reset Link"}
          </button>
        </form>

        <div className="flex items-center justify-between mt-5 flex-wrap gap-2">
          {mode !== "register" ? (
            <button type="button" data-testid="login-switch-register" onClick={() => { setMode("register"); setError(""); setNotice(""); }}
                    className="font-mono" style={{ minHeight: 44, fontSize: 12, color: teal, background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}>
              New here? Create an account
            </button>
          ) : (
            <button type="button" data-testid="login-switch-login" onClick={() => { setMode("login"); setError(""); setNotice(""); }}
                    className="font-mono" style={{ minHeight: 44, fontSize: 12, color: teal, background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}>
              Have an account? Sign in
            </button>
          )}
          {mode === "login" && (
            <button type="button" data-testid="login-forgot-btn" onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                    className="font-mono" style={{ minHeight: 44, fontSize: 12, color: "rgba(224,224,224,0.6)", background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}>
              Forgot password?
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
